import React from 'react';
import { useOrderTypeContext } from '../../Contexts/OrderTypeContext';
import CustomerService from '../../Services/CustomerService';
import { useLoginContext } from '../../Contexts/LoginContext';
import ButtonPrimary from './ButtonPrimary';
import Button from './Button';
import { IMaskInput } from 'react-imask';
import { TbCircle, TbCircleCheckFilled, TbCircleFilled, TbX } from 'react-icons/tb';
import { useLayoutContext } from '../../Contexts/LayoutContext';
import { Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { getProfileGenderList } from '../../Utils/Constants/Global';
import ConfirmMobilePhone from '../Layout/Profile/ConfirmMobilePhone';
import { isCpfValid } from '../../Utils/Helpers';
import { useTranslation } from 'react-i18next';
import { MdVerified } from 'react-icons/md';

const getValidationSchema = (t: any) => 
    Yup.object().shape({
        first_name: Yup.string()
            .min(2, t("form.validation.invalid_min_characters", {quantity: 2}))
            .required(t('form.validation.required')),
        last_name: Yup.string()
            .min(2, t("form.validation.invalid_min_characters", {quantity: 2}))
            .required(t('form.validation.required')),
        email: Yup.string()
            .required(t('form.validation.required'))
            .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, t('form.validation.invalid_email')),
        mobile_phone: Yup.string()
            .min(11, t('form.validation.invalid_mobile_phone'))
            .required(t('form.validation.required')),
        cpf: Yup.string()
            .length(11, t('form.validation.invalid_document_cpf'))
            .required(t('form.validation.required'))
            .test('is-valid-cpf', t('form.validation.invalid_document_cpf'), (value) => isCpfValid(value || '')),
        gender: Yup.number()
});

interface FormProfileProps {
    onCancel?: CallableFunction;
    onSuccess?: CallableFunction;
    buttonLabel?: string;
}

const FormProfile = ({
    onCancel,
    onSuccess,
    buttonLabel,
    }: FormProfileProps) => {

    const { t } = useTranslation();
    const validationSchema = getValidationSchema(t);

    const { orderType, isStoreCounter } = useOrderTypeContext();
    const { user, setUser } = useLoginContext();
    const { isMobile } = useLayoutContext();
    const { setMainLoading, setMainLoadingIcon, setMainLoadingIconError, setMainLoadingIconSuccess, setMainLoadingTitle, setMainLoadingMessage, setMainLoadingCloseButton, setMainLoadingAutoClose } = useLayoutContext();

    const initialProfileData = {
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        mobile_phone: user.mobile_phone,
        cpf: user.cpf,
        gender: user.gender,
    }

    const handleUpdateProfile = async (params: any) => {
        try {
            setMainLoading(true);
            setMainLoadingTitle(t('profile.form_update_profile.loading_title'));
            setMainLoadingMessage(t('layout.loading'));

            const customerService = new CustomerService(orderType);
            const payload = {
                first_name: params.first_name.trim(),
                last_name: params.last_name.trim(),
                cpf: params.cpf,
                gender: params.gender,
                mobile_phone: user.mobile_phone_confirmed ? user.mobile_phone : params.mobile_phone,
                email: user.is_confirmed ? user.email : params.email
            }
            const updatedProfile = isStoreCounter
                ? await customerService.updateProfileStoreCounter(payload)
                : await customerService.updateProfile(payload);

            if (updatedProfile.profile) {
                setUser(updatedProfile.profile);

                setMainLoadingIcon(false);
                setMainLoadingIconSuccess(true);
                setMainLoadingTitle(t('profile.form_update_profile.success_title'));
                setMainLoadingMessage('');
                setMainLoadingCloseButton(true);
                setMainLoadingAutoClose(true);

                onSuccess?.();
            }
        } catch (error: any) {
            const errorMessage = error.response.data?.errors?.email;
            setMainLoadingIcon(false);
            setMainLoadingIconError(true);
            setMainLoadingTitle(t('errors.default_title'));
            setMainLoadingMessage(errorMessage ? errorMessage : t('profile.form_update_profile.error_message'));
            setMainLoadingCloseButton(true);
        }
    }

    const isFieldRequired = (schema: any, fieldName: string) => {
        const field = schema.describe().fields[fieldName];
        return field.tests.some((test: any) => test.name === 'required');
    };

    return (
        <>
            <Formik
                initialValues={initialProfileData}
                validationSchema={validationSchema}
                validateOnMount={true}
                onSubmit={(values) => handleUpdateProfile(values)}
                >
                {({ setFieldValue, values }) => (
                    <Form className="flex flex-col gap-6 justify-between h-full pb-20 md:pb-0">

                        <div className='w-full flex flex-col gap-4 h-full'>

                            <div className='w-full'>
                                <span className='font-semibold text-shopfood-title'>
                                    {t('profile.form_update_profile.first_name.label')}
                                    {isFieldRequired(validationSchema, 'first_name') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                </span>
                                <IMaskInput
                                    id="first_name"
                                    autoComplete='on'
                                    name="first_name"
                                    mask={/^[a-zA-ZÀ-ÿ\s]*$/}
                                    value={values.first_name}
                                    onAccept={(value) => setFieldValue('first_name', value)} // Atualiza o valor no Formik
                                    unmask={false} // true|false|'typed'
                                    placeholder={t('profile.form_update_profile.first_name.placeholder')}
                                    className="relative block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                />
                                <ErrorMessage name="first_name" component="div" className='text-xs text-shopfood-error pt-1' />
                            </div>

                            <div className='w-full'>
                                <span className='font-semibold text-shopfood-title'>
                                    {t('profile.form_update_profile.last_name.label')}
                                    {isFieldRequired(validationSchema, 'last_name') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                </span>
                                <IMaskInput
                                    id="last_name"
                                    name="last_name"
                                    mask={/^[a-zA-ZÀ-ÿ\s]*$/}
                                    value={values.last_name}
                                    onAccept={(value) => setFieldValue('last_name', value)} // Atualiza o valor no Formik
                                    unmask={false} // true|false|'typed'
                                    placeholder={t('profile.form_update_profile.last_name.placeholder')}
                                    className="relative block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                />
                                <ErrorMessage name="last_name" component="div" className='text-xs text-shopfood-error pt-1' />
                            </div>

                            <div className='w-full'>
                                <span className='font-semibold text-shopfood-title'>
                                    {t('profile.form_update_profile.email.label')}
                                    {isFieldRequired(validationSchema, 'email') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                </span>
                                <div className="relative flex items-center w-full">
                                    <IMaskInput
                                        id="email"
                                        name="email"
                                        autoComplete='on'
                                        mask={/^[a-zA-Z0-9@._+-]*$/}
                                        value={values.email}
                                        disabled={user.is_confirmed || user.email}
                                        onAccept={(value) => setFieldValue('email', value)} // Atualiza o valor no Formik
                                        unmask={false} // true|false|'typed'
                                        placeholder={t('profile.form_update_profile.email.placeholder')}
                                        className="relative block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                    />
                                    {(user.is_confirmed || user.email) && 
                                        <div className="absolute right-2 flex gap-0.5 items-center text-xs">
                                            <MdVerified className='text-sm text-shopfood-success' />
                                            <span className='leading-none text-nowrap text-gray-400 mr-1 italic font-medium'>{t('profile.basic_info.email.verified_label')}</span>
                                        </div>
                                    }
                                </div>

                                <ErrorMessage name="email" component="div" className='text-xs text-shopfood-error pt-1' />
                            </div>

                            <div className='w-full md:max-w-80 border-red-400'>
                                <span className='font-semibold text-shopfood-title'>
                                    {t('profile.form_update_profile.mobile_phone.label')}
                                    {isFieldRequired(validationSchema, 'mobile_phone') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                </span>
                                <div className='relative flex items-center justify-between'>
                                    <IMaskInput
                                        id="mobile_phone"
                                        name="mobile_phone"
                                        type="numeric"
                                        mask={'(00) 00000-0000'}
                                        value={values.mobile_phone}
                                        disabled={user.mobile_phone_confirmed}
                                        onAccept={(value) => setFieldValue('mobile_phone', value)} // Atualiza o valor no Formik
                                        unmask={true} // true|false|'typed'
                                        placeholder={t('profile.form_update_profile.mobile_phone.placeholder')}
                                        className="relative block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                    />
                                    <ConfirmMobilePhone phone={user.mobile_phone} confirmed={user.mobile_phone_confirmed} onSuccess={() => {}} className='absolute right-2' />
                                </div>
                                <ErrorMessage name="mobile_phone" component="div" className='text-xs text-shopfood-error pt-1' />
                            </div>

                            <div className='w-full max-w-64'>
                                <span className='font-semibold text-shopfood-title'>
                                    {t('profile.form_update_profile.document_cpf.label')}
                                    {isFieldRequired(validationSchema, 'cpf') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                </span>
                                <IMaskInput
                                    id="cpf"
                                    name="cpf"
                                    mask={"000.000.000-00"}
                                    value={values.cpf}
                                    onAccept={(value) => setFieldValue('cpf', value)} // Atualiza o valor no Formik
                                    unmask={true} // true|false|'typed'
                                    placeholder="___.___.___-__"
                                    className="relative block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                />
                                <ErrorMessage name="cpf" component="div" className='text-xs text-shopfood-error pt-1' />
                            </div>

                            <div className='w-full flex flex-col md:flex-row md:items-center md:gap-2'>
                                <span className='font-semibold text-shopfood-title'>
                                    {t('profile.form_update_profile.gender.label')}<span className='hidden md:inline'>:</span>
                                </span>
                                <div className='flex w-full justify-between gap-1 ring-1 ring-gray-100 rounded-lg p-1'>
                                    { getProfileGenderList().map((gender: any) => (
                                        <Button
                                            gray
                                            key={gender.type}
                                            onClick={() => setFieldValue('gender', gender.type)}
                                            className={`
                                                w-full flex-col md:flex-row gap-0.5 h-max text-sm
                                                rounded-lg ring-inset
                                                ${gender.type === values.gender && 'pointer-events-none text-custom-primary bg-white hover:ring-transparent'}
                                            `}
                                            >
                                                { gender.type === values.gender 
                                                    ? <TbCircleCheckFilled size={20} /> 
                                                    : <TbCircle size={20} className='text-gray-400' />
                                                }
                                                { t(`profile.gender.${gender.type}`) }
                                            </Button>
                                    ))}
                                </div>
                            </div>

                        </div>

                        <div className='flex gap-2 w-full'>
                            <ButtonPrimary type='submit' className='w-full md:w-auto' onClick={() => {}}>{ buttonLabel ? buttonLabel : t('profile.form_update_profile.button_submit_label') }</ButtonPrimary>
                            {onCancel && !isMobile && <Button onClick={() => onCancel()} className='w-full md:w-auto'>{t('layout.confirm.cancel')}</Button>}
                            {onCancel && isMobile && <Button rounded onClick={() => onCancel()}><span><TbX size={20} /></span></Button>}
                        </div>

                    </Form>

                )}
            </Formik>
        </>
    )
}

export default FormProfile;
